import { mdiAccountPlus } from '@mdi/js';
import { Currency } from 'common/classes/currency';
import { BaseTable, ColumnProp } from 'common/components/table/base';
import { TableTitle } from 'common/components/tables';
import { CurrencyCodes } from 'common/configs/currency';
import { Account, Xrates } from 'common/state/accounts/actions';
import { Wallet } from 'common/state/banking/actions';
import { Bet } from 'common/state/bets/actions';
import { Customer, CustomerSummary } from 'common/state/customers/actions';
import React, { useMemo } from 'react';

export interface AccountSummaryTableProps {
  accounts: Account[];
  bets: Bet[];
  customer: Customer;
  summaries: CustomerSummary[];
  wallets: Wallet[];
  xrates: Xrates;
  isLoading?: boolean;
}

export interface AccountSummaryTableRow {
  currency: CurrencyCodes;
  balance: number;
  walletBalance: number;
  minBalance: number;
  credit: number;
  threshold: number;
  lastPlacementDate: string;
}

export const AccountSummaryTable = ({
  bets,
  customer,
  summaries,
  wallets,
  xrates,
  isLoading,
}: AccountSummaryTableProps) => {
  const rows = useMemo((): AccountSummaryTableRow[] => {
    if (!customer?.default_currency || !xrates) return [];
    const row: AccountSummaryTableRow = {
      currency: customer.default_currency,
      balance: 0,
      walletBalance: 0,
      minBalance: 0,
      credit: 0,
      threshold: 0,
      lastPlacementDate: null,
    };

    summaries?.forEach((summary: CustomerSummary) => {
      if (summary.wallet_threshold) {
        row.threshold +=
          (xrates[customer.default_currency] *
            parseFloat(summary.wallet_threshold)) /
          xrates[summary.wallet_currency];
      }
      row.credit +=
        (xrates[customer.default_currency] *
          parseFloat(summary.wallet_credit)) /
        xrates[summary.wallet_currency];
      if (summary.wallet_min_balance) {
        row.minBalance +=
          (xrates[customer.default_currency] *
            parseFloat(summary.wallet_min_balance)) /
          xrates[summary.wallet_currency];
      }
      row.balance +=
        (xrates[customer.default_currency] *
          (parseFloat(summary.balances.cleared) +
            parseFloat(summary.balances.reconciled) +
            parseFloat(summary.balances.pending) +
            parseFloat(summary.balances.outstanding) +
            parseFloat(summary.balances.adjustments) +
            parseFloat(summary.balances.deposits))) /
        xrates[summary.wallet_currency];
    });

    wallets?.forEach((wallet: Wallet) => {
      row.walletBalance +=
        (xrates[customer.default_currency] * parseFloat(wallet.balance)) /
        xrates[wallet.currency];
    });

    row.lastPlacementDate = bets?.[0]?.placed_at;

    return [row];
  }, [bets, customer, summaries, wallets, xrates]);

  const columns = useMemo(
    (): ColumnProp<AccountSummaryTableRow>[] => [
      { key: 'currency', title: 'Currency' },
      {
        key: 'balance',
        title: 'Client Account Balance',
        valueFormatter: (row) =>
          new Currency(row.balance, row.currency).toElement(),
        csvFormatter: (row) =>
          new Currency(row.balance, row.currency).toString({ symbol: false }),
      },
      {
        key: 'minBalance',
        title: 'Min Balance',
        valueFormatter: (row) =>
          new Currency(row.minBalance, row.currency).toElement({
            colour: false,
          }),
        csvFormatter: (row) =>
          new Currency(row.minBalance, row.currency).toString({
            symbol: false,
          }),
      },
      {
        key: 'topup',
        title: 'Top Up',
        valueFormatter: (row) =>
          row.balance < row.minBalance
            ? new Currency(
                row.balance - row.minBalance,
                row.currency
              ).toElement()
            : 'No',
        csvFormatter: (row) =>
          row.balance < row.minBalance
            ? new Currency(row.balance - row.minBalance, row.currency).toString(
                { symbol: false }
              )
            : 'No',
      },
      {
        key: 'credit',
        title: 'Credit',
        valueFormatter: (row) =>
          new Currency(row.credit, row.currency).toElement({
            colour: false,
          }),
        csvFormatter: (row) =>
          new Currency(row.credit, row.currency).toString({ symbol: false }),
      },
      {
        key: 'threshold',
        title: 'Profit / Losses - Settlement Threshold',
        width: 190,
        valueFormatter: (row) =>
          new Currency(row.threshold, row.currency).toElement({
            colour: false,
          }),
        csvFormatter: (row) =>
          new Currency(row.threshold, row.currency).toString({ symbol: false }),
      },
      {
        key: 'lastPlacementDate',
        title: 'Last Placement Date',
        valueFormatter: (row) =>
          row?.lastPlacementDate
            ? new Date(row.lastPlacementDate).toLocaleString()
            : '-',
      },
    ],
    []
  );

  return (
    <>
      <TableTitle icon={mdiAccountPlus} title="Account Summary" />
      <BaseTable
        rows={rows}
        columns={columns}
        csvName="Accounts"
        paginate={false}
        isLoading={isLoading}
      />
    </>
  );
};
